import type { RiskAssessmentTemplate } from '@gcv/shared';
import { useQuery } from '@tanstack/react-query';

import { RiskAssessmentTemplatesApi } from 'api/RiskAssessmentTemplatesApi';
import { RISK_ASSESSMENT_TEMPLATES_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

/** `GET /risk-assessment-templates/:id` is not routed yet. Flip on release, delete the fallback. */
const HAS_TEMPLATE_RECORD_ENDPOINT = false;

/** Keyed by `RiskAssessmentTemplate.id` — the pinned record, not the logical id settings routes on. */
export function useRiskAssessmentTemplate(recordId?: string) {
  const riskAssessmentTemplatesApi = new RiskAssessmentTemplatesApi();
  const { userBankId } = useUser();

  const {
    data: riskAssessmentTemplate,
    isLoading: isLoadingRiskAssessmentTemplate,
    isError: isErrorRiskAssessmentTemplate
  } = useQuery<RiskAssessmentTemplate | null>({
    enabled: !!userBankId.length && !!recordId,
    queryKey: [RISK_ASSESSMENT_TEMPLATES_KEY, userBankId, 'record', recordId],
    queryFn: async () => {
      if (HAS_TEMPLATE_RECORD_ENDPOINT) {
        return await riskAssessmentTemplatesApi.getRiskAssessmentTemplate(userBankId, recordId as string);
      }

      // The list filters on version label, so only a current draft/published pin resolves. No
      // last_updated_only — it collapses to one record and hides the published one behind a draft.
      const templates = await riskAssessmentTemplatesApi.getRiskAssessmentTemplatesForBank(userBankId, {
        version: 'draft,latest'
      });

      return templates.find((template) => template.id === recordId) ?? null;
    }
  });

  return {
    riskAssessmentTemplate: riskAssessmentTemplate ?? undefined,
    isLoadingRiskAssessmentTemplate,
    isErrorRiskAssessmentTemplate
  };
}
